// UsageChart.jsx
// npm install recharts

import { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import { styles } from "./style.js";
import {
  rawData,
  GROUP_OPTIONS,
  METRIC_OPTIONS,
} from "./useageData.js";

function formatYAxis(value) {
  if (value >= 1_000_000) return `${value / 1_000_000}M`;
  if (value >= 1_000) return `${value / 1_000}K`;
  return value;
}

function formatTooltip(value, metric) {
  if (metric === "Cost") {
    return `$${Number(value).toFixed(2)}`;
  }

  return Number(value).toLocaleString();
}

const CustomTooltip = ({
  active,
  payload,
  label,
  metric,
}) => {
  if (!active || !payload?.length) return null;

  return (
    <div style={styles.tooltip}>
      <p style={styles.tooltipLabel}>{label}</p>

      {payload.map((p) => (
        <p
          key={p.name}
          style={{
            ...styles.tooltipRow,
            color: p.color,
          }}
        >
          <span style={styles.tooltipDot(p.color)} />
          {p.name}:{" "}
          <strong>
            {formatTooltip(p.value, metric)}
          </strong>
        </p>
      ))}
    </div>
  );
};

export default function UsageChart() {
  const [groupBy, setGroupBy] = useState("Daily");
  const [metric, setMetric] = useState("Minutes");

  const [openGroup, setOpenGroup] = useState(false);
  const [openMetric, setOpenMetric] = useState(false);

  const metricMap = {
    Minutes: {
      key: "minutes",
      color: "#6abfa3",
    },
    Calls: {
      key: "calls",
      color: "#4f8cff",
    },
    Cost: {
      key: "cost",
      color: "#ff9f43",
    },
  };

  const currentMetric = metricMap[metric];

  return (
    <div style={styles.card}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.controls}>
          <Dropdown
            label={`View: ${groupBy}`}
            options={GROUP_OPTIONS}
            selected={groupBy}
            open={openGroup}
            onToggle={() => {
              setOpenGroup(!openGroup);
              setOpenMetric(false);
            }}
            onSelect={(value) => {
              setGroupBy(value);
              setOpenGroup(false);
            }}
          />

          <Dropdown
            label={`Metric: ${metric}`}
            options={METRIC_OPTIONS}
            selected={metric}
            open={openMetric}
            onToggle={() => {
              setOpenMetric(!openMetric);
              setOpenGroup(false);
            }}
            onSelect={(value) => {
              setMetric(value);
              setOpenMetric(false);
            }}
          />
        </div>
      </div>

      {/* Chart */}
      <div style={styles.chartWrap}>
        <ResponsiveContainer
          width="100%"
          height={280}
        >
          <AreaChart
            data={rawData}
            margin={{
              top: 10,
              right: 16,
              left: 8,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient
                id="gradMetric"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={currentMetric.color}
                  stopOpacity={0.45}
                />
                <stop
                  offset="95%"
                  stopColor={currentMetric.color}
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              stroke="#f0f0f0"
              strokeDasharray="0"
              vertical={false}
            />

            <XAxis
              dataKey="date"
              tick={styles.axisTick}
              axisLine={false}
              tickLine={false}
              dy={8}
            />

            <YAxis
              tickFormatter={formatYAxis}
              tick={styles.axisTick}
              axisLine={false}
              tickLine={false}
              width={45}
            />

            <Tooltip
              content={
                <CustomTooltip metric={metric} />
              }
            />

            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="plainline"
              formatter={(value) => (
                <span style={styles.legendLabel}>
                  {value}
                </span>
              )}
            />

            <Area
              type="monotone"
              dataKey={currentMetric.key}
              name={metric}
              stroke={currentMetric.color}
              strokeWidth={2}
              fill="url(#gradMetric)"
              dot={false}
              activeDot={{
                r: 4,
                strokeWidth: 0,
                fill: currentMetric.color,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────────────── */
/* Dropdown */
/* ────────────────────────────── */

function Dropdown({
  label,
  options,
  selected,
  open,
  onToggle,
  onSelect,
}) {
  return (
    <div style={styles.dropWrap}>
      <button
        style={styles.dropBtn}
        onClick={onToggle}
      >
        {label}

        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            marginLeft: 6,
            opacity: 0.5,
          }}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div style={styles.dropMenu}>
          {options.map((option) => (
            <div
              key={option}
              style={{
                ...styles.dropItem,
                ...(option === selected
                  ? styles.dropItemActive
                  : {}),
              }}
              onClick={() => onSelect(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}